import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";

import { Loader2 } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/sonner";
import { EVENTO_NAO_AUTENTICADO, conectarEventos, desconectarEventos, useBootstrap } from "@/api";
import { useTituloPagina } from "@/lib/use-titulo-pagina";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          O endereço acessado não existe ou foi movido.
        </p>
        <div className="mt-6">
          <Link
            to="/loja"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Ir para o Flow
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  const mensagem = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-foreground">Algo deu errado</h1>
        <p className="mt-2 text-sm text-muted-foreground">{mensagem}</p>
        <div className="mt-6">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

/** Rotas que não usam o chrome da aplicação (sidebar + topbar). */
const PREFIXOS_SEM_CHROME = ["/login", "/convite"];

const semChrome = (pathname: string) =>
  pathname === "/" || PREFIXOS_SEM_CHROME.some((p) => pathname.startsWith(p));

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const comChrome = !semChrome(pathname);

  useTituloPagina(pathname);

  return (
    <QueryClientProvider client={queryClient}>
      {comChrome ? (
        <Sessao>
          <SidebarProvider>
            <div className="flex min-h-screen w-full bg-background">
              <AppSidebar />
              <div className="flex min-h-screen flex-1 flex-col">
                <Outlet />
              </div>
            </div>
          </SidebarProvider>
        </Sessao>
      ) : (
        <Outlet />
      )}
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}

/**
 * Hidrata o registry síncrono, mantém o cache em dia pelo WebSocket e guarda
 * as rotas da aplicação.
 *
 * O bootstrap é a própria prova de sessão: se responde 401, o cliente já
 * tentou o refresh e falhou, então não há o que renderizar além do login.
 * A autorização de verdade continua no servidor — isto evita a tela piscar
 * vazia, não protege dado nenhum.
 */
function Sessao({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isLoading, isError } = useBootstrap();

  useEffect(() => {
    const aoPerderSessao = () => navigate({ to: "/login", replace: true });
    window.addEventListener(EVENTO_NAO_AUTENTICADO, aoPerderSessao);
    return () => window.removeEventListener(EVENTO_NAO_AUTENTICADO, aoPerderSessao);
  }, [navigate]);

  useEffect(() => {
    if (isError) navigate({ to: "/login", replace: true });
  }, [isError, navigate]);

  useEffect(() => {
    conectarEventos(queryClient);
    return () => desconectarEventos();
  }, [queryClient]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) return null;

  return <>{children}</>;
}
